import { isNil } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';
import { v4 as uuidv4 } from 'uuid';

import addressBooksSlice from '../../redux/addressBooksSlice';
import { BasketSelectors } from '../../redux/basket';
import userSlice from '../../redux/userSlice';
import { FORM } from '../../constants';
import { SHIPMENT_FIELDS } from '../../constants/forms';
import { GB } from '../../constants/strings';
import { AddressHelpers, FormatterHelpers } from '../../helpers';
import { getConfigByNetwork } from '../../helpers/network';

const defaultEmptyAddress = {
  organisation: '',
  // property: deliveryAddress.property, there is no mapping on server side
  street: '',
  locality: '',
  town: '',
  county: '',
  postcode: '',
  countryCode: '',
  [FORM.FIELDS.ADDRESS_KEY.KEY]: '',
};

export const getDeliveryAddress = (
  _state,
  { deliveryCountryCode, deliveryAddress }
) => {
  if (!deliveryAddress) {
    return {
      ...defaultEmptyAddress,
      countryCode: deliveryCountryCode,
    };
  }

  const { addressLine1, addressLine2 } = AddressHelpers.transformAddressValues({
    street: deliveryAddress.street,
    locality: deliveryAddress.locality,
    property: deliveryAddress.property,
  });

  return {
    organisation: deliveryAddress.organisation,
    // property: deliveryAddress.property, there is no mapping on server side
    street: addressLine1,
    locality: addressLine2,
    town: deliveryAddress.town,
    county: deliveryAddress.county,
    postcode: deliveryAddress.postcode,
    countryCode: deliveryCountryCode,
    [FORM.FIELDS.ADDRESS_KEY.KEY]: deliveryAddress.key,
    _coordinates: AddressHelpers.formatCoordinates(deliveryAddress),
  };
};

export const getDeliverTo = (_state, { network }) => {
  const networkConfig = getConfigByNetwork(network);
  return networkConfig?.deliverTo || null;
};

export const getDefaultCollectionAddress = createSelector(
  addressBooksSlice.selectors.getDefaultAddressBook,
  (_state, { collectionDetailedAddress }) => collectionDetailedAddress,
  (defaultAddress, collectionDetailedAddress) =>
    // Return only country in case if default address is unavailable
    isNil(defaultAddress)
      ? {
          countryCode: GB,
        }
      : {
          addressId: collectionDetailedAddress?.Key,
          countryCode: defaultAddress.countryCode,
          postcode: defaultAddress.postcode,
          county: defaultAddress.addressLine4,
          town: defaultAddress.addressLine3,
          organisation: defaultAddress.organisation,
          street: defaultAddress.addressLine1,
          locality: defaultAddress.addressLine2,
          _coordinates: collectionDetailedAddress
            ? AddressHelpers.formatCoordinates(collectionDetailedAddress)
            : null,
        }
);

export const getDefaultCollectionContactDetails = createSelector(
  addressBooksSlice.selectors.getDefaultAddressBook,
  userSlice.selectors.getAccountDetailsData,
  (defaultAddress, accountDetails) => {
    if (!isNil(defaultAddress)) {
      return {
        _addressBookId: defaultAddress.addressBookId,
        contactName: defaultAddress.contactName,
        telephone: defaultAddress.phoneNumber,
        email: defaultAddress.email,
      };
    }

    if (!isNil(accountDetails)) {
      return {
        _addressBookId: null,
        contactName: FormatterHelpers.joinStringsWith(
          [accountDetails.firstName, accountDetails.lastName],
          ' '
        ),
        telephone: accountDetails.phone,
        email: accountDetails.email,
      };
    }

    return null;
  }
);

export const getCollectFrom = (_state, { network }) => {
  const networkConfig = getConfigByNetwork(network);
  return networkConfig?.collectFrom || null;
};

const getDefaultExporterDetails = createSelector(
  addressBooksSlice.selectors.getDefaultAddressBook,
  defaultAddress => {
    if (!isNil(defaultAddress)) {
      return {
        eoriNumber: defaultAddress.eoriNumber,
        vatNumber: defaultAddress.vatNumber,
        ukimsNumber: defaultAddress.ukimsNumber,
      };
    }
    return null;
  }
);

export const getShipmentDto = createStructuredSelector({
  [SHIPMENT_FIELDS.DESTINATION_ADDRESS.KEY]: getDeliveryAddress,
  [SHIPMENT_FIELDS.DELIVER_TO.KEY]: getDeliverTo,
  [SHIPMENT_FIELDS.COLLECTION_ADDRESS.KEY]: getDefaultCollectionAddress,
  'outboundConsignment.collectionDetails.contactDetails':
    getDefaultCollectionContactDetails,
  [SHIPMENT_FIELDS.COLLECT_FROM.KEY]: getCollectFrom,
  'outboundConsignment.shippingRef2': BasketSelectors.getBasketId,
  [SHIPMENT_FIELDS.PARCELS.KEY]: (state, { weight }) => [
    {
      _parcelId: uuidv4(),
      _weight: weight ? String(weight) : '',
    },
  ],
  [SHIPMENT_FIELDS.NETWORK.KEY]: (state, { network }) => network,
  [SHIPMENT_FIELDS.TOTAL_WEIGHT.KEY]: (state, { weight }) =>
    weight ? String(weight) : undefined,
  'invoice.exporterDetails': getDefaultExporterDetails,
});
